import { clsx } from "clsx";
import { IconCheck } from "@tabler/icons-react";
import type { MouseEvent, ReactNode } from "react";
import { Text } from "../typography";

export interface CheckboxFieldProps {
  label?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  name?: string;
  helperText?: ReactNode;
  variant?: "card" | "inline";
  stopPropagation?: boolean;
  className?: string;
}

export function CheckboxField({
  label,
  checked,
  onChange,
  disabled = false,
  name,
  helperText,
  variant = "card",
  stopPropagation = false,
  className,
}: CheckboxFieldProps) {
  const handleClick = (event: MouseEvent<HTMLLabelElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }
  };

  if (variant === "inline") {
    return (
      <label
        className={clsx(
          // `relative` anchors the sr-only input inside the label; otherwise focusing it on
          // click scrolls the whole page to where it sits in the document (blank screen).
          "relative inline-flex cursor-pointer items-center gap-3",
          disabled && "cursor-not-allowed opacity-60",
          className,
        )}
        onClick={handleClick}
      >
        <input
          type="checkbox"
          name={name}
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />
        <span
          aria-hidden="true"
          className={clsx(
            "inline-flex size-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors",
            checked
              ? "border-brandBlack bg-brandBlack text-textOnDark"
              : "border-borderDefault bg-bgWhite text-transparent",
          )}
        >
          <IconCheck size={12} />
        </span>
        {label ? (
          <Text variant="bodySm" tone="body" inline>
            {label}
          </Text>
        ) : null}
      </label>
    );
  }

  return (
    <label
      className={clsx(
        // `relative`: see the inline variant above.
        "relative flex cursor-pointer items-start gap-3 rounded-2xl border border-borderDefault bg-bgWhite px-4 py-3 transition-colors hover:border-borderDark",
        checked && "border-brandPrimary bg-brandPrimary/5",
        disabled && "cursor-not-allowed opacity-60 hover:border-borderDefault",
        className,
      )}
      onClick={handleClick}
    >
      <input
        type="checkbox"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <span
        aria-hidden="true"
        className={clsx(
          "mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-[6px] border transition-colors",
          checked
            ? "border-brandBlack bg-brandBlack text-textOnDark"
            : "border-borderDefault bg-bgWhite text-transparent",
        )}
      >
        <IconCheck size={14} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        {label ? (
          <Text variant="bodySmBold" tone="dark" inline>
            {label}
          </Text>
        ) : null}
        {helperText ? (
          <Text variant="bodySm" tone="muted">
            {helperText}
          </Text>
        ) : null}
      </span>
    </label>
  );
}

export default CheckboxField;
